import React, {useEffect, useMemo} from "react"
import {BackButton} from "../../components/BackButton/BackButton"
import {useHistory, useLocation} from "react-router-dom"
import {useFormik} from "formik"
import {useDispatch, useSelector} from "react-redux"
import {StateType} from "../../store/store"
import * as yup from "yup"
import {Helmet} from "react-helmet-async"
import {Button, Checkbox, Form, Tag} from "antd"
import {FormInput} from "../../components/Form/FormInput"
import {FormTextarea} from "../../components/Form/FormTextarea"
import {changeObjectGroup, createObjectGroup} from "../../store/objectGroupReducer"
import {getCompanies, setCompanies} from "../../store/companyReducer"
import {FormSelect} from "../../components/Form/FormSelect"
import {JsonFormRules} from "../../components/Form/JSON/JsonFormRules"
import {timezones} from "../../helps/timezones"
import {ObjectGroupResponse} from "../../models/response"

const schema = yup.object().shape({
    name: yup.string().required('Введите название').max(250, 'Максимальная длина 250 символов'),
    idCompany: yup.number().required('Выберите компанию').min(1, 'Выберите компанию'),
    description: yup.string().max(2000, 'Максимальная длина 2000 символов'),
    attributes: yup.string().required('Введите атрибуты').max(5000, 'Максимальная длина 5000 символов'),
})

export const ObjectGroupForm = () => {
    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation<{ objectGroup: ObjectGroupResponse, idCompany?: number }>()
    const objectGroup = location?.state?.objectGroup
    const idCompany = location?.state?.idCompany
    const status = useSelector((state: StateType) => state.appReducer.status)
    const companies = useSelector((state: StateType) => state.companyReducer.companies)
    const controller = useMemo(() => new AbortController(), [])

    useEffect(() => {
        dispatch(getCompanies({excludeSystem: true, controller}))

        return () => {
            controller.abort()
            dispatch(setCompanies(null))
        }
    }, [dispatch, controller])

    const formik = useFormik({
        initialValues: {
            name: objectGroup?.name || '',
            idCompany: objectGroup?.idCompany || idCompany,
            active: objectGroup ? objectGroup.active : true,
            description: objectGroup?.description || '',
            attributes: objectGroup?.attributes || '[]',
            timeOffset: objectGroup?.timeOffset || timezones[2].value,
        },
        validationSchema: schema,
        onSubmit: async (values) => {
            let res: any
            if (objectGroup) {
                res = await dispatch(changeObjectGroup({
                    id: objectGroup.id,
                    name: values.name,
                    idCompany: values.idCompany || 0,
                    active: values.active,
                    description: values.description,
                    attributes: values.attributes,
                    timeOffset: values.timeOffset,
                    controller,
                }))
            } else {
                res = await dispatch(createObjectGroup({
                    name: values.name,
                    idCompany: values.idCompany || 0,
                    active: values.active,
                    description: values.description,
                    attributes: values.attributes,
                    timeOffset: values.timeOffset,
                    controller,
                }))
            }
            if (res?.payload?.status === 0) {
                if (!objectGroup)
                {
                    history.replace(`/objectGroup/${res.payload.data.id}`)
                }
                else
                {
                    history.goBack()
                }
            }
        },
    })
    const {values, handleChange, handleBlur, handleSubmit, errors, touched, setFieldValue} = formik

    useEffect(() => {
        if (companies?.data && !values?.idCompany) {
            setFieldValue('idCompany', companies.data[0]?.id)
        }
    }, [companies, values?.idCompany])

    const companyOptions = useMemo(() => companies?.data?.map(i => ({value: i.id, label: i.name})) || [], [companies])

    return (
        <div className="form-page">
            <Helmet>
                <title>{objectGroup ? 'Обновление группы' : 'Новая группа'}</title>
            </Helmet>
            <Form className="form-container"
                  autoComplete="off"
                  onSubmitCapture={handleSubmit}
            >
                <div className="description-header"><BackButton onClick={() => history.goBack()}/></div>

                <div className="description-title">{objectGroup ? 'Обновление группы' : 'Новая группа'}</div>

                <FormInput placeholder="Название"
                           value={values.name}
                           name="name"
                           onBlur={handleBlur}
                           onChange={handleChange}
                           isInvalid={touched.name && errors.name}
                           allowClear
                />
                <FormSelect placeholder="Компания"
                            value={values.idCompany || ''}
                            onChange={(value) => setFieldValue('idCompany', value)}
                            options={companyOptions}
                            className="form-select"
                            isInvalid={touched.idCompany && errors.idCompany}
                />
                <FormSelect placeholder="Часовой пояс"
                            value={values.timeOffset}
                            onChange={(value) => setFieldValue('timeOffset', value)}
                            options={timezones}
                            className="form-select"
                            isInvalid={touched.timeOffset && errors.timeOffset}
                />
                <FormTextarea placeholder="Описание"
                              value={values.description}
                              name="description"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              isInvalid={touched.description && errors.description}
                              allowClear
                              autoSize={{minRows: 2}}
                />

                <div style={{marginTop: 20}}>
                    <Checkbox checked={values.active}
                              name="active"
                              onChange={handleChange}
                    >
                        <Tag color={values.active ? 'green' : 'default'}>Активно</Tag>
                    </Checkbox>
                </div>

                <div className="form-button-block">
                    <Button type="primary"
                            htmlType="submit"
                            loading={status === 'loading'}
                    >
                        Сохранить
                    </Button>
                    <Button type="default"
                            onClick={() => history.goBack()}
                    >
                        Отмена
                    </Button>
                </div>
            </Form>

            {
                objectGroup
                    ? <div style={{marginTop: 30}}>
                        <JsonFormRules grid={`objparam_grid${objectGroup.id}`}
                                       attributes={values.attributes}
                                       setAttributes={(value) => setFieldValue('attributes', value)}
                                       isInvalid={errors.attributes}
                        />
                    </div>
                    : null
            }
        </div>
    )
}

